import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useCreateUser } from "@src/presentation/hooks/users"
import { useRoles } from "@src/presentation/hooks/rbac"
import { useUniversities } from "@src/presentation/hooks/universities"
import { ToastService } from "@src/shared/services/toast.service"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Input,
    Select,
    SelectItem,
    Divider,
    Spinner,
    Chip
} from "@heroui/react"
import {
    ChevronLeft,
    UserPlus,
    Mail,
    Shield,
    Building2,
    AlertCircle
} from "lucide-react"

export default function CreateAdminPage() {
    const navigate = useNavigate()
    const [formData, setFormData] = useState({
        email: "",
        role_ids: [] as string[],
        university_id: undefined as string | undefined,
    })

    const [errors, setErrors] = useState<Record<string, string>>({})

    // Fetch roles
    const { data: rolesData, isLoading: isLoadingRoles } = useRoles()
    const roles = rolesData || []

    // Fetch universities
    const { data: universitiesData, isLoading: isLoadingUniversities } = useUniversities()
    const universities = universitiesData?.universities || []

    // Create user mutation
    const { mutate: createUser, isPending } = useCreateUser({
        onSuccess: () => {
            ToastService.success("User created successfully! They will receive an email to complete their profile.")
            navigate("/users")
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to create user")
        },
        retry: false,
    })

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (isPending) return

        // Validation
        const newErrors: Record<string, string> = {}

        if (!formData.email) {
            newErrors.email = "Email is required"
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address"
        }

        if (formData.role_ids.length === 0) {
            newErrors.role_ids = "At least one role is required"
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        createUser({
            email: formData.email,
            role_ids: formData.role_ids,
            university_id: formData.university_id,
        })
    }

    if (isLoadingRoles) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <Spinner size="lg" color="primary" />
                    <p className="mt-4 text-sm text-default-500 font-medium">Loading roles...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-none">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                                startContent={<ChevronLeft className="h-4 w-4" />}
                                className="hover:scale-105 transition-transform flex-shrink-0"
                            >
                                Back
                            </Button>
                            <Divider orientation="vertical" className="h-6 hidden sm:block" />
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md">
                                    <UserPlus className="h-4 w-4 text-primary-foreground" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        Create New User
                                    </h1>
                                    <p className="text-xs text-default-500 font-medium">
                                        Add a new user to the system
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="max-w-2xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email */}
                        <Card className="shadow-none border border-border bg-secondary rounded overflow-visible">
                            <CardHeader className="border-b border-divider pb-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-primary/10">
                                        <Mail className="h-4 w-4 text-primary" />
                                    </div>
                                    <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        Email Address
                                    </h2>
                                </div>
                            </CardHeader>
                            <CardBody className="space-y-4 overflow-visible">
                                <Input
                                    type="email"
                                    label="Email"
                                    placeholder="user@example.com"
                                    value={formData.email}
                                    onChange={(e) => {
                                        setFormData({ ...formData, email: e.target.value })
                                        if (errors.email) setErrors({ ...errors, email: "" })
                                    }}
                                    isInvalid={!!errors.email}
                                    errorMessage={errors.email}
                                    startContent={<Mail className="h-4 w-4 text-default-400" />}
                                    classNames={{
                                        input: "font-medium",
                                        inputWrapper: "rounded border border-border"
                                    }}
                                    isRequired
                                    description="The user will receive an email to complete their profile setup"
                                />
                            </CardBody>
                        </Card>

                        {/* Role & Access */}
                        <Card className="border border-border rounded bg-secondary shadow-none">
                            <CardHeader className="border-b border-divider pb-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-success/10">
                                        <Shield className="h-4 w-4 text-success" />
                                    </div>
                                    <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        Role & Access
                                    </h2>
                                </div>
                            </CardHeader>
                            <CardBody className="space-y-4">
                                <Select
                                    label="Roles"
                                    placeholder="Select roles"
                                    selectionMode="multiple"
                                    selectedKeys={new Set(formData.role_ids)}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys) as string[]
                                        setFormData({ ...formData, role_ids: selected })
                                        if (errors.role_ids) setErrors({ ...errors, role_ids: "" })
                                    }}
                                    isInvalid={!!errors.role_ids}
                                    errorMessage={errors.role_ids}
                                    startContent={<Shield className="h-4 w-4 text-default-400" />}
                                    classNames={{
                                        trigger: "shadow-none rounded border border-border",
                                        popoverContent: "rounded border border-border",
                                    }}
                                    isRequired
                                    renderValue={(items) => {
                                        return (
                                            <div className="flex flex-wrap gap-2">
                                                {items.map((item) => (
                                                    <Chip key={item.key} size="sm" variant="flat" color="primary">
                                                        {item.textValue}
                                                    </Chip>
                                                ))}
                                            </div>
                                        )
                                    }}
                                >
                                    {roles.map((role) => (
                                        <SelectItem key={role.roleId} textValue={role.displayName} className="rounded">
                                            <div className="flex flex-col">
                                                <span className="font-semibold">{role.displayName}</span>
                                                {role.description && (
                                                    <span className="text-xs text-default-500">{role.description}</span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </Select>
                            </CardBody>
                        </Card>

                        {/* University (Optional) */}
                        <Card className="border border-border rounded bg-secondary shadow-none">
                            <CardHeader className="border-b border-divider pb-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-warning/10">
                                        <Building2 className="h-4 w-4 text-warning" />
                                    </div>
                                    <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        University (Optional)
                                    </h2>
                                </div>
                            </CardHeader>
                            <CardBody className="space-y-4">
                                <Select
                                    label="University"
                                    placeholder="Select university (optional)"
                                    selectedKeys={formData.university_id ? new Set([formData.university_id]) : new Set()}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys) as string[]
                                        setFormData({ ...formData, university_id: selected[0] || undefined })
                                    }}
                                    startContent={<Building2 className="h-4 w-4 text-default-400" />}
                                    classNames={{
                                        trigger: "shadow-none rounded border border-border",
                                        popoverContent: "rounded border border-border",
                                    }}
                                    isDisabled={isLoadingUniversities}
                                    description="Pre-assign a university to this user. They can change it during onboarding."
                                >
                                    {universities.map((university) => (
                                        <SelectItem key={university.university_id} textValue={university.name} className="rounded">
                                            <div className="flex flex-col">
                                                <span className="font-semibold">{university.name}</span>
                                                {university.code && (
                                                    <span className="text-xs text-default-500">Code: {university.code}</span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </Select>
                            </CardBody>
                        </Card>

                        {/* Information Notice */}
                        <Card className="border border-border rounded shadow-none bg-gradient-to-br from-primary/5 to-primary/10">
                            <CardBody className="p-4">
                                <div className="flex gap-3">
                                    <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-foreground mb-1">
                                            How it works
                                        </p>
                                        <ul className="text-xs text-default-600 space-y-1 list-disc list-inside">
                                            <li>The user will receive an OTP to their email</li>
                                            <li>They'll verify their email and complete their profile</li>
                                            <li>During onboarding, they'll set their name and confirm university</li>
                                            <li>The assigned role(s) will be applied immediately</li>
                                        </ul>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Action Buttons */}
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="flat"
                                onPress={() => navigate(-1)}
                                className="shadow-none rounded"
                                isDisabled={isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                startContent={<UserPlus className="h-4 w-4" />}
                                className="shadow-lg shadow-primary/30 font-semibold rounded"
                                isLoading={isPending}
                                isDisabled={isPending}
                            >
                                Create User
                            </Button>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    )
}
